import { useEffect, useMemo, useState } from "react";
import { Check, X, RefreshCw, AlertCircle } from "lucide-react";
import { api, type CurriculumCandidate } from "../../../api";
import { Eyebrow, Select, Badge } from "../../../components/ui";
import { Button } from "../../../components/ui/Button";
import { cn } from "../../../lib/format";

const LANGS = [
  { value: "de", label: "German" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "ru", label: "Russian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Mandarin" },
  { value: "nl", label: "Dutch" },
  { value: "ar", label: "Arabic" },
];

type Kind = "vocab" | "grammar";
type Status = "pending" | "judged" | "approved" | "rejected" | "all";

export default function Curriculum() {
  const [lang, setLang] = useState("de");
  const [kind, setKind] = useState<Kind>("vocab");
  const [status, setStatus] = useState<Status>("judged");
  const [rows, setRows] = useState<CurriculumCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.listCandidates({ lang, kind, status });
      setRows(res.candidates);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load failed");
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, kind, status]);

  const pending = useMemo(() => rows.length, [rows]);

  return (
    <div>
      <Eyebrow>curriculum review queue</Eyebrow>
      <p className="mt-2 max-w-2xl font-serif italic text-ink-soft text-base">
        Vocab and grammar harvested from real learner-tutor turns. The LLM judge has
        already pre-ranked these — your job is a glance + click. Approved entries
        merge into the runtime curriculum within ~5 min, no redeploy needed.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-ink-faint mb-1">Language</label>
          <Select value={lang} onChange={(e) => setLang(e.target.value)}>
            {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </Select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-ink-faint mb-1">Kind</label>
          <Select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
            <option value="vocab">Vocabulary</option>
            <option value="grammar">Grammar</option>
          </Select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-ink-faint mb-1">Status</label>
          <Select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
            <option value="pending">Pending (not yet judged)</option>
            <option value="judged">Judged (ready for review)</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </Select>
        </div>
        <Button variant="ghost" onClick={() => void load()} disabled={busy}>
          <RefreshCw size={14} className={cn("mr-1.5", busy && "animate-spin")} />
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <div className="ml-auto text-sm text-ink-soft">
          {pending} candidate{pending === 1 ? "" : "s"}
        </div>
      </div>

      {err && (
        <div className="mt-6 flex items-start gap-2 rounded border border-terracotta/40 bg-terracotta/5 p-3 text-sm text-ink">
          <AlertCircle size={16} className="text-terracotta shrink-0 mt-0.5" />
          <div>{err}</div>
        </div>
      )}

      {!busy && rows.length === 0 && !err && (
        <div className="mt-12 text-center text-ink-soft text-sm">
          No candidates match these filters yet. Run the judge: <code className="font-mono">python scripts/run_curriculum_judge.py --lang {lang}</code>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <CandidateCard
            key={`${r.lang}-${kind}-${r.lemma || r.tag}`}
            candidate={r}
            kind={kind}
            onChange={() => void load()}
          />
        ))}
      </div>
    </div>
  );
}

function CandidateCard({
  candidate, kind, onChange,
}: { candidate: CurriculumCandidate; kind: Kind; onChange: () => void }) {
  const isVocab = kind === "vocab";
  const key = isVocab ? (candidate.lemma || "") : (candidate.tag || "");

  // Editable fields, prefilled with the LLM judge's defaults.
  const [pos, setPos] = useState(candidate.pos || candidate.judged_pos || "");
  const [gender, setGender] = useState(candidate.judged_gender || "");
  const [level, setLevel] = useState((isVocab ? candidate.judged_level : candidate.judged_proposed_level) || "");
  const [gloss, setGloss] = useState(candidate.judged_gloss || "");
  const [name, setName] = useState(candidate.judged_proposed_name || "");
  const [summary, setSummary] = useState(candidate.judged_proposed_summary || "");
  const [tier, setTier] = useState(String(candidate.judged_proposed_tier || 5));
  const [busy, setBusy] = useState(false);

  async function approve() {
    setBusy(true);
    try {
      await api.approveCandidate({
        lang: candidate.lang, kind, key,
        level, pos, gender, gloss, name, summary,
        tier: parseInt(tier, 10) || 5,
        id: candidate.judged_proposed_id,
      });
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : "approve failed");
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await api.rejectCandidate({ lang: candidate.lang, kind, key });
      onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : "reject failed");
    } finally {
      setBusy(false);
    }
  }

  const conf = candidate.judged_confidence ?? 0;
  const isJudged = candidate.status === "judged";
  const valid = candidate.judged_valid !== false;
  const covered = candidate.judged_covered === true;

  // Visual warnings
  const lowConf = conf < 0.5;
  const mostlyErrors =
    isVocab &&
    (candidate.error_flagged_count ?? 0) > (candidate.total_count ?? 0) / 2;

  return (
    <div className="rounded-md border border-[rgba(42,36,26,0.10)] bg-paper-soft p-4">
      <div className="flex items-baseline gap-3">
        <div className="font-mono text-base text-ink">{key}</div>
        <Badge>{candidate.total_count} obs · {candidate.distinct_users} users</Badge>
        {isJudged && (
          <Badge tone={valid && !lowConf ? "neutral" : "warn"}>
            {isVocab
              ? (valid ? `valid · conf ${conf.toFixed(2)}` : `INVALID · conf ${conf.toFixed(2)}`)
              : (covered ? `covered · conf ${conf.toFixed(2)}` : `GAP · conf ${conf.toFixed(2)}`)}
          </Badge>
        )}
        {mostlyErrors && <Badge tone="warn">mostly in error turns</Badge>}
        {candidate.status === "approved" && <Badge tone="good">approved</Badge>}
        {candidate.status === "rejected" && <Badge tone="warn">rejected</Badge>}
      </div>

      {candidate.judged_notes && (
        <div className="mt-2 text-xs text-ink-soft italic">{candidate.judged_notes}</div>
      )}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {isVocab ? (
          <>
            <FieldRow label="Level" value={level} onChange={setLevel} placeholder="A1/A2/B1…" />
            <FieldRow label="POS" value={pos} onChange={setPos} placeholder="noun/verb/adj/adv/function/idiom" />
            <FieldRow label="Gender" value={gender} onChange={setGender} placeholder="m/f/n or empty" />
            <FieldRow label="Gloss" value={gloss} onChange={setGloss} placeholder="short EN gloss" />
          </>
        ) : (
          <>
            <FieldRow label="Level" value={level} onChange={setLevel} placeholder="A1/A2/B1…" />
            <FieldRow label="Tier" value={tier} onChange={setTier} placeholder="1-6" />
            <FieldRow label="Name" value={name} onChange={setName} placeholder="short concept name" wide />
            <FieldRow label="Summary" value={summary} onChange={setSummary} placeholder="1-3 sentences with example" wide multi />
          </>
        )}
      </div>

      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-wider text-ink-faint">
          {isVocab ? "Example" : `Examples (${candidate.examples?.length || 0})`}
        </div>
        {isVocab ? (
          <div className="mt-1 font-serif text-sm text-ink-soft">
            "{candidate.example_sentence || "(none)"}"
          </div>
        ) : (
          <ul className="mt-1 space-y-0.5 font-serif text-sm text-ink-soft">
            {(candidate.examples || []).slice(-5).map((ex, i) => (
              <li key={i} className="before:content-['—_']">{ex}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={approve} disabled={busy || candidate.status === "approved"}>
          <Check size={14} className="mr-1.5" /> Approve
        </Button>
        <Button variant="ghost" onClick={reject} disabled={busy || candidate.status === "rejected"}>
          <X size={14} className="mr-1.5" /> Reject
        </Button>
      </div>
    </div>
  );
}

function FieldRow({
  label, value, onChange, placeholder, wide, multi,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  wide?: boolean;
  multi?: boolean;
}) {
  return (
    <div className={cn(wide && "md:col-span-2")}>
      <label className="block text-[11px] uppercase tracking-wider text-ink-faint mb-0.5">
        {label}
      </label>
      {multi ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full rounded border border-[rgba(42,36,26,0.20)] bg-paper px-2 py-1.5 text-sm font-serif"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded border border-[rgba(42,36,26,0.20)] bg-paper px-2 py-1.5 text-sm font-mono"
        />
      )}
    </div>
  );
}
